/**
 * Clue atom semantics.
 *
 * Two views of every atom:
 *  - `evaluateAtom` — is this statement true of a complete arrangement? Used to enumerate the true
 *    clues of a generated puzzle and to validate authored ones.
 *  - `unaryCandidates` — which cells could this character occupy considering only this atom and the
 *    board? Used to seed the solver's domains. Relational atoms return null and are enforced during
 *    search instead.
 *
 * Semantics worth restating, because they are what make the puzzle work:
 *  - `beside` = orthogonally adjacent AND in the same room; a wall breaks adjacency.
 *  - offsets and compass directions are NOT room-constrained — they speak about the whole grid.
 *  - `alone` means nobody else in that room, the victim included.
 */

import { besideCells, colOf, isRoomCorner, rowOf } from "./grid";
import type { Atom, Board, CellIdx, GeneralClue, ObjectKind } from "./types";

/** A complete arrangement: the cell occupied by each character index. */
export type Arrangement = readonly CellIdx[];

export const roomAt = (board: Board, cell: CellIdx): number => board.regions[cell] as number;

/** The kind of furniture on a cell, or null for bare floor. */
export function kindAt(board: Board, cell: CellIdx): ObjectKind | null {
  const index = board.objectAt[cell] as number;
  return index < 0 ? null : (board.objects[index]?.kind ?? null);
}

/** True when a cell has an object of `kind` orthogonally adjacent within the same room. */
export function isBeside(board: Board, cell: CellIdx, kind: ObjectKind): boolean {
  return besideCells(board, cell).some((n) => kindAt(board, n) === kind);
}

const occupantsOfRoom = (board: Board, arrangement: Arrangement, room: number): number[] =>
  arrangement.reduce<number[]>((acc, cell, char) => {
    if (roomAt(board, cell) === room) acc.push(char);
    return acc;
  }, []);

export function evaluateAtom(atom: Atom, board: Board, arrangement: Arrangement): boolean {
  const cell = arrangement[atom.char] as CellIdx;
  const size = board.size;

  switch (atom.kind) {
    case "room":
      return roomAt(board, cell) === atom.roomId;
    case "notRoom":
      return roomAt(board, cell) !== atom.roomId;
    case "orRoom":
      return atom.roomIds.includes(roomAt(board, cell));
    case "on":
      return kindAt(board, cell) === atom.objectKind;
    case "onlyOn":
      return (
        kindAt(board, cell) === atom.objectKind &&
        arrangement.every((c, i) => i === atom.char || kindAt(board, c) !== atom.objectKind)
      );
    case "beside":
      return isBeside(board, cell, atom.objectKind);
    case "notBeside":
      return !isBeside(board, cell, atom.objectKind);
    case "onlyBeside":
      return (
        isBeside(board, cell, atom.objectKind) &&
        arrangement.every((c, i) => i === atom.char || !isBeside(board, c, atom.objectKind))
      );
    case "otherBeside": {
      // "Someone else was beside a <kind> in their area."
      const room = roomAt(board, cell);
      return arrangement.some(
        (c, i) => i !== atom.char && roomAt(board, c) === room && isBeside(board, c, atom.objectKind),
      );
    }
    case "alone":
      return occupantsOfRoom(board, arrangement, roomAt(board, cell)).length === 1;
    case "with":
      return (
        atom.other !== atom.char &&
        roomAt(board, cell) === roomAt(board, arrangement[atom.other] as CellIdx)
      );
    case "aloneWith": {
      const room = roomAt(board, cell);
      const occupants = occupantsOfRoom(board, arrangement, room);
      return occupants.length === 2 && occupants.includes(atom.other);
    }
    case "absRow":
      return rowOf(cell, size) === atom.row;
    case "absCol":
      return colOf(cell, size) === atom.col;
    case "rowOffset": {
      const other = arrangement[atom.other] as CellIdx;
      const delta = rowOf(other, size) - rowOf(cell, size);
      // North means further up the grid, i.e. a smaller row index.
      return atom.dir === "N" ? delta === atom.distance : delta === -atom.distance;
    }
    case "colOffset": {
      const other = arrangement[atom.other] as CellIdx;
      const delta = colOf(other, size) - colOf(cell, size);
      // East means further right, i.e. a larger column index.
      return atom.dir === "E" ? delta === -atom.distance : delta === atom.distance;
    }
    case "compass": {
      const other = arrangement[atom.other] as CellIdx;
      const dr = rowOf(cell, size) - rowOf(other, size);
      const dc = colOf(cell, size) - colOf(other, size);
      const north = dr < 0;
      const east = dc > 0;
      if (dr === 0 || dc === 0) return false;
      switch (atom.dir) {
        case "NE":
          return north && east;
        case "NW":
          return north && !east;
        case "SE":
          return !north && east;
        case "SW":
          return !north && !east;
      }
    }
    // eslint-disable-next-line no-fallthrough -- every compass case returns
    case "inCorner":
      return isRoomCorner(board, cell);
    case "notCorner":
      return !isRoomCorner(board, cell);
  }
}

export function evaluateGeneralClue(
  clue: GeneralClue,
  board: Board,
  arrangement: Arrangement,
): boolean {
  switch (clue.kind) {
    case "noEmptyRoom": {
      const occupied = new Set(arrangement.map((cell) => roomAt(board, cell)));
      return occupied.size === board.rooms.length;
    }
    case "oneOnType":
      return arrangement.filter((cell) => kindAt(board, cell) === clue.objectKind).length === 1;
  }
}

/**
 * Cells this character could occupy considering only this atom and the board geometry.
 * Returns null for relational atoms, which depend on where other characters end up.
 */
export function unaryCandidates(atom: Atom, board: Board): Set<CellIdx> | null {
  const size = board.size;
  const cells = Array.from({ length: size * size }, (_, i) => i);
  const keep = (predicate: (cell: CellIdx) => boolean): Set<CellIdx> =>
    new Set(cells.filter(predicate));

  switch (atom.kind) {
    case "room":
      return keep((c) => roomAt(board, c) === atom.roomId);
    case "notRoom":
      return keep((c) => roomAt(board, c) !== atom.roomId);
    case "orRoom":
      return keep((c) => atom.roomIds.includes(roomAt(board, c)));
    // `onlyOn`/`onlyBeside` also assert exclusivity, which the solver checks separately, but the
    // positional half is unary and worth pruning with here.
    case "on":
    case "onlyOn":
      return keep((c) => kindAt(board, c) === atom.objectKind);
    case "beside":
    case "onlyBeside":
      return keep((c) => isBeside(board, c, atom.objectKind));
    case "notBeside":
      return keep((c) => !isBeside(board, c, atom.objectKind));
    case "absRow":
      return keep((c) => rowOf(c, size) === atom.row);
    case "absCol":
      return keep((c) => colOf(c, size) === atom.col);
    case "inCorner":
      return keep((c) => isRoomCorner(board, c));
    case "notCorner":
      return keep((c) => !isRoomCorner(board, c));
    default:
      return null;
  }
}

/** Atoms that name a row, column or direction — the powerful kind we ration by difficulty. */
export function isPositionalAtom(atom: Atom): boolean {
  return (
    atom.kind === "absRow" ||
    atom.kind === "absCol" ||
    atom.kind === "rowOffset" ||
    atom.kind === "colOffset" ||
    atom.kind === "compass"
  );
}
